import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import { primeLeagueContext } from "../../../utils/useLeagueContext";
import DetailGame from "../DetailGame";
import LeagueJoinPreview, { landingContainerClass } from "../landing/LeagueJoinPreview";
import LeagueInviteGate from "../landing/LeagueInviteGate";
import LeagueHome from "./LeagueHome";

// /contest/:slug — the door into a league.
//
// A MEMBER goes straight inside: the league's Home, with the feed and the
// standings. There's no longer an intermediate "Let's Go!" screen between joining
// and using the league.
//
// Everyone else still gets DetailGame, which is the join gate (and the public
// league's shop window). That page is untouched — a league is a full working site
// and its non-member path has its own rules; only the member landing changed.
const LeagueEntry = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  const resolve = useCallback(async () => {
    try {
      const res = await getGameFromSlug({ slug });
      // A bad slug answers with HTTP 200 + success:false, so read the body.
      const data = res?.data?.success === false ? null : res?.data?.data;
      const resolvedGame = data?.id ? data : null;
      setGame(resolvedGame);
      // This screen's fetch is the freshest read of the league there is, so the
      // shell shares it rather than repeating the request.
      primeLeagueContext(slug, resolvedGame);
    } catch (e) {
      console.error("League lookup failed:", e);
      setGame(null);
    } finally {
      setResolved(true);
    }
  }, [slug]);

  useEffect(() => {
    setResolved(false);
    resolve();
  }, [resolve]);

  // The session flags the old "Let's Go!" button used to set on the way in. The
  // league's inner screens (Trade, Create Post) read them, so entering through
  // Home has to leave the same state behind or those screens see a half-open
  // league.
  useEffect(() => {
    if (!game?.isMember) return;
    localStorage.setItem("game_type", game.game_type);
    if (game.game_type === "private") localStorage.setItem("isAuthenticated", "true");
  }, [game]);

  if (!resolved) {
    return (
      <div className="mt-16 md:mt-24 pb-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="h-36 md:h-44 rounded-2xl bg-[#F3F4F6] animate-pulse" />
        </div>
      </div>
    );
  }

  // No such league. The older detail page only bounced these to League Play
  // after a flash of its own screen, so go there directly. Client: "Reachable
  // with a current equivalent: reroute it to the current screen."
  if (!game) return <Navigate to="/contest" replace />;

  // An email-invitation link (?token=…). For a public league the current
  // equivalent is the join preview below, which anyone may join from (client:
  // "invite links should open the League join preview"). Email invites are
  // retired; this private-league branch and the older page behind it go in the
  // post-launch removal PR. Links for leagues no longer on the site already take
  // the `!game` path above.
  const hasInvitationToken = new URLSearchParams(search).has("token");
  if (hasInvitationToken && game.game_type === "private" && !game.isMember) {
    return <DetailGame onJoined={resolve} />;
  }

  // A private league reached by its address is not discoverable: knowing the slug
  // is not the credential, so a non-member sees the invite gate rather than any of
  // the league's content. Public leagues are meant to be found, so they show their
  // shop window.
  if (!game.isMember && game.game_type === "private") return <LeagueInviteGate />;

  // Not a member yet: the league's shop window. Joining from here re-resolves and
  // drops the new member straight inside. LeagueJoinPreview picks the layout — a
  // public listing pitches, a private invite reveals.
  if (!game.isMember) {
    return (
      // Client Rev 2: "Tap the Share icon ... Return to the landing page. I can
      // no longer scroll far enough down to reach the Join Now button."
      //
      // The Join League CTA is the last thing on this page, and this wrapper's
      // bottom padding is the only thing holding it clear of the app's fixed
      // bottom navigation (.bottom-navbar-container, 64px, position: fixed).
      // Measured at the end of the scroll: pb-12 (48px) + the card's own p-5
      // (21px) left the button exactly 5px above the bar — the same 5px at every
      // viewport height, because both are anchored to the bottom of the page.
      //
      // Five pixels is not a margin, it is a coincidence. On iOS Safari a fixed
      // element is anchored to the LAYOUT viewport, whose bottom ~88px sits
      // behind Safari's own toolbar whenever that toolbar is expanded — and
      // returning from the native share sheet is exactly when Safari restores it
      // expanded. The page is already at maximum scroll by then, so there is no
      // further downward gesture left to collapse the toolbar again: the last
      // band of the page, the nav and the 5px above it, stays behind the toolbar
      // and the button cannot be reached. A reload (the client's log out / log
      // back in) puts the page at the top, so the next scroll collapses the
      // toolbar and it works again — until the next share.
      //
      // That 128px of mobile bottom padding is GONE, and the reason it can go is
      // that the premise above no longer holds: the Join / Enter CTA is not the
      // last thing in the page flow any more. Every landing page here now docks
      // its CTA over the viewport on mobile and reserves exactly the room that
      // dock needs, measured from the dock itself — CashGameLandingPage via
      // `--cash-dock-pad`, the public and private cards via `--invite-dock-pad`.
      // A fixed bar cannot be pushed behind the navigation or behind Safari's
      // toolbar by a short page, which is what the padding was defending against.
      //
      // Client: "The public leagues landing page on mobile has too much white
      // space when you scroll down. private leagues and cash games landing page
      // is perfect. Match that, please."
      //
      // Public was the only one still paying BOTH: this 128px and its own dock
      // reservation, stacked. The private landing is hosted by
      // PrivateLeaguePreview, which dropped its mobile padding when it got a
      // dock, and the Cash Game page had been cancelling this one out with a
      // `-mb-32` of its own — so the two the client calls perfect are the two
      // that already pay it once. Removing the source here and the cash page's
      // counterweight with it leaves cash pixel-identical and brings public into
      // line, rather than adding a third compensation.
      //
      // Desktop has no bottom navigation and no dock, so it keeps its 48px.
      <div className="mt-16 md:mt-24 pb-0 md:pb-12">
        {/* ONE column width for all three landing pages.
            Client: "Match the main content container width used on the Public
            and Private League landing pages. The current Cash Games layout uses
            too much of the available screen width, and there is enough white
            space to condense it."

            A cash game used to take `max-w-screen-xl` here, on an earlier
            instruction to match the league dashboard's page width. That case is
            gone rather than narrowed, because the answer is now the same for
            every competition type and a conditional that always picks the same
            branch is just a thing to misread later.

            THE CASH GAME IS WIDE AGAIN, and only the cash game. The client's new
            desktop landing design is a two-column page — "The right-side card
            becomes sticky and stops at the end of the contest rules card" — and
            it is drawn in a 920px column split 580 + 300. At `max-w-screen-md`
            the rail leaves ~400px beside it, which is narrower than the phone
            the left column's contents were drawn for, so the layout the client
            has just approved cannot be built at that width.

            Public and private keep `max-w-screen-md` exactly as they are: they
            are still single-column pages and the instruction that set their
            width is untouched. Flagged to the client, since it is their own
            earlier "Match the main content container width used on the Public
            and Private League landing pages" that this parts company with. */}
        <div className={landingContainerClass(game)}>
          <LeagueJoinPreview game={game} onJoined={resolve} />
        </div>
      </div>
    );
  }

  return <LeagueHome game={game} />;
};

export default LeagueEntry;
